import { HostState } from './host-state.js'
import { ClassPlaceCount, ShowPlaceCount } from './place-count.js'
import { EntryCode, historicalIdentifierOrder } from './entry-code.js'
import { z } from 'zod'
import { ContactField } from './person-contact.js'
import { Slug } from './slug.js'

/** Portable business data, deliberately independent of database and HTTP schemas. */
const Key = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/)
const Text = z.string()
const BusinessCode = Text
const Name = Text
const Count = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER)
const NumberValue = z.number().min(-Number.MAX_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER)
const Time = z.iso.datetime({ offset: true })
const Digest = z.string().regex(/^sha256:[a-f0-9]{64}$/)
const NullableText = Text.nullable().optional()
const NullableTime = Time.nullable().optional()
const Ref = Key.nullable().optional()
const Origin = z.strictObject({ namespace: Key, recordId: Key })
const row = {
  key: Key,
  origin: Origin.optional(),
  originAliases: z.array(Origin).optional(),
  createdAt: Time.optional(),
  updatedAt: Time.optional(),
}
const Contact = z.strictObject({
  email: Text.nullable().optional(),
  phone: Text.nullable().optional(),
  mailingAddress: Text.nullable().optional(),
  mailingCity: Text.nullable().optional(),
  mailingState: Text.nullable().optional(),
  mailingZipCode: Text.nullable().optional(),
  displayCity: Text.nullable().optional(),
  displayState: Text.nullable().optional(),
})
export const ShowPackagePointsConfig = z.strictObject({
  placementPoints: z.record(BusinessCode, NumberValue),
  tiebreakers: z.array(
    z.enum([
      'mostFirsts',
      'mostSeconds',
      'mostThirds',
      'mostFourths',
      'mostFifths',
      'mostSixths',
      'mostPlacements',
      'fewestEntries',
    ])
  ),
  allowTies: z.boolean(),
  applyTiebreakers: z.boolean(),
})
const awardTypes = [
  'class',
  'section',
  'best_of_show',
  'hero_award',
  'best_in_show',
  'best_in_division',
  'grand_champion',
  'reserve_grand',
  'special',
] as const
const eligibility = z.strictObject({ staffEligible: z.boolean().optional() }).optional()
const AwardsPolicy = z.strictObject({
  awardEligibility: z.record(Text, eligibility).optional(),
  bestInDivisionEnabled: z.boolean().optional(),
  heroAwardEnabled: z.boolean().optional(),
  clubSpiritAwardEnabled: z.boolean().optional(),
  clubChampionAwardEnabled: z.boolean().optional(),
})

/** Allowlist mirrors the seeded rule vocabulary, with no opaque JSON escape hatch. */
export const ShowPackageRules = z.strictObject({
  container: z
    .strictObject({
      type: Text,
      material: Text.optional(),
      color: Text.optional(),
      embossedAllowed: z.boolean().optional(),
      lid: z.strictObject({ type: Text, notes: Text.optional() }).optional(),
      fillGuidance: Text.optional(),
    })
    .optional(),
  quantity: z
    .strictObject({
      pieces: Count.optional(),
      setSizes: z.array(Count).optional(),
      netWeightOzMin: NumberValue.optional(),
      netWeightOzMax: NumberValue.optional(),
      volumeMl: NumberValue.optional(),
      weightOz: NumberValue.optional(),
    })
    .optional(),
  subcategories: z.array(Text).optional(),
  labels: z.strictObject({ placement: Text, extras: z.array(Text).optional() }).optional(),
  labelsPerExhibit: z.number().int().min(1).max(100).optional(),
  attachments: z.array(Text).optional(),
  judgingCriteria: z.array(Text).optional(),
  forbidden: z.array(Text).optional(),
  notes: Text.optional(),
})
const Provenance = z.strictObject({
  origin: z.enum(['legacy', 'app', 'wpcivi', 'authored']),
  sourceInstanceId: NullableText,
  sourceKey: NullableText,
  importedDigest: Digest.nullable().optional(),
  publicationVersion: NullableText,
})
const Model = z.strictObject({
  ...row,
  name: Name,
  description: NullableText,
  categoryCode: BusinessCode,
  categoryName: Name,
  aliases: z
    .union([Text, z.array(Text)])
    .nullable()
    .optional(),
  revision: Count,
  retired: z.boolean(),
  rules: ShowPackageRules,
  flags: z.strictObject({ novice: z.boolean().optional() }).nullable().optional(),
  provenance: Provenance.optional(),
})
const Subtype = z.strictObject({
  ...row,
  modelKey: Key,
  name: Name,
  description: NullableText,
  retired: z.boolean(),
  revision: Count,
  provenance: Provenance.optional(),
})
const ModelSnapshot = z.strictObject({
  kind: z.enum(['adopted', 'legacy_baseline']),
  modelKey: Ref,
  subtypeKey: Ref,
  capturedAt: Time.optional(),
  adoptedAt: NullableTime,
  adoptedBy: Origin.nullable().optional(),
  modelRevision: Count.optional(),
  subtypeRevision: Count.optional(),
  modelProvenance: Provenance.optional(),
  subtypeSnapshot: z
    .strictObject({
      modelKey: Key.optional(),
      name: Name,
      description: NullableText,
      retired: z.boolean(),
      provenance: Provenance.optional(),
    })
    .nullable()
    .optional(),
  defaults: z
    .strictObject({
      name: Name,
      description: NullableText,
      categoryCode: BusinessCode,
      categoryName: Name,
      rules: ShowPackageRules,
      flags: z.strictObject({ novice: z.boolean().optional() }).nullable().optional(),
    })
    .nullable(),
})
const RuleSnapshot = z.strictObject({
  ...row,
  sourceSystem: Text,
  sourceType: NullableText,
  sourceId: NullableText,
  sourceChecksum: Text.optional(),
  rulesVersion: NullableText,
  title: NullableText,
  status: z.enum(['approved', 'pending_approval', 'superseded']),
  approvedAt: NullableTime,
  approvedBy: Origin.nullable().optional(),
  createdBy: Origin.nullable().optional(),
  snapshot: z.strictObject({
    selectedCategories: z.array(Text).optional(),
    sections: z
      .array(
        z.strictObject({
          key: Key,
          title: Text,
          order: Count,
          blocks: z.array(
            z.strictObject({
              key: Key,
              title: Text,
              body: Text,
              order: Count,
              classKeys: z.array(Key).optional(),
            })
          ),
        })
      )
      .optional(),
    criteria: z
      .array(
        z.strictObject({ name: Name, maxPoints: Count.min(1), weight: NumberValue.positive() })
      )
      .optional(),
  }),
})
const Division = z.strictObject({
  ...row,
  code: BusinessCode,
  name: Name,
  order: NumberValue.int(),
})
const Class = z.strictObject({
  ...row,
  divisionKey: Key,
  code: BusinessCode,
  name: Name,
  order: NumberValue.int(),
  description: NullableText,
  displayName: NullableText,
  rules: ShowPackageRules.nullable().optional(),
  modelKey: Ref,
  subtypeKey: Ref,
  modelDefaults: ModelSnapshot.nullable().optional(),
  modelLinkValidated: z.boolean().optional(),
  maxEntriesPerExhibitor: Count.nullable().optional(),
  ruleOverrides: ShowPackageRules.nullable().optional(),
  parameters: z
    .strictObject({
      labelsPerExhibit: z.number().int().min(1).max(100).optional(),
      theme: Text.optional(),
      featured: z.boolean().optional(),
      judging: z.strictObject({ judgeAssignmentKeys: z.array(Key) }).optional(),
    })
    .nullable()
    .optional(),
  noAwards: z.boolean(),
  placeCount: ClassPlaceCount.optional(),
  grandChampionEligible: z.boolean(),
  judgingStartedAt: NullableTime,
  resultsFinalizedAt: NullableTime,
})
const Entrant = z.strictObject({
  ...row,
  exhibitorKey: Key,
  heroAwardEligible: z.boolean(),
  grandChampionEligible: z.boolean(),
  clubKey: Ref,
  clubName: NullableText,
  finalizedAt: NullableTime,
  finalizedBy: Origin.nullable().optional(),
  finalizedByRole: NullableText,
})
const Fingerprint = z.strictObject({
  algorithm: z.literal('honeyshow-entry-intent.local-ids.v1'),
  value: z.string().regex(/^[a-f0-9]{64}$/),
  originNamespace: Key,
  references: z
    .strictObject({ classKey: Key, exhibitorKey: Key, clubKey: Key.nullable() })
    .optional(),
})
const Entry = z.strictObject({
  ...row,
  classKey: Key,
  exhibitorKey: Key,
  clubKey: Ref,
  clubName: NullableText,
  exhibitId: EntryCode,
  title: NullableText,
  notes: NullableText,
  fees: NumberValue.nonnegative(),
  status: z.enum(['draft', 'checked_in', 'not_entered', 'judged', 'disqualified', 'finalized']),
  submission: z
    .strictObject({ id: Key, fingerprint: Fingerprint.nullable() })
    .nullable()
    .optional(),
  overrideDuplicateReason: NullableText,
  overrideDuplicateBy: Origin.nullable().optional(),
  overrideDuplicateAt: NullableTime,
  notEnteredAt: NullableTime,
  notEnteredBy: Origin.nullable().optional(),
  notEnteredByRole: NullableText,
  notEnteredReason: NullableText,
  disqualifiedAt: NullableTime,
  disqualifiedBy: Origin.nullable().optional(),
  disqualifiedReason: NullableText,
})
const Exhibit = z.strictObject({
  ...row,
  entryKey: Key,
  stagingLocation: NullableText,
  labelState: z.enum(['queued', 'printed', 'error', 'emailed']),
})
const Identifier = z.strictObject({
  ...row,
  value: EntryCode,
  kind: z.enum(['allocated', 'historical']),
  orderIndex: Count,
  assignedEntryKey: Key.nullable(),
  assignedExhibitorKey: Key.nullable(),
  assignedAt: Time.nullable(),
  source: z
    .enum([
      'preallocated',
      'pending',
      'pending_manual',
      'reserved_exhibitor',
      'seed_reserved',
      'seed',
      'seed_demo_show',
      'manual',
      'backfill',
      'auto',
      'void',
      'historical_import',
    ])
    .nullable(),
})
const Tombstone = z.strictObject({
  ...row,
  submissionId: Key,
  deletedEntryKey: Key,
  deletedAt: Time,
})
const Score = z.strictObject({
  ...row,
  exhibitKey: Key,
  judgeKey: Ref,
  judgeOrigin: Origin.nullable().optional(),
  criterion: Name,
  points: NumberValue.nonnegative(),
  comment: NullableText,
})
const Award = z.strictObject({
  ...row,
  exhibitKey: Key.nullable(),
  entrantKey: Key.nullable(),
  divisionKey: Ref,
  type: z.enum(awardTypes),
  place: Name,
  notes: NullableText,
})
const Series = z.strictObject({
  ...row,
  isChampionship: z.boolean().optional(),
  organizationKey: Key,
  name: Name,
  slug: Slug,
  revision: Count,
})
const SeriesIdentity = z.strictObject({
  ...row,
  seriesKey: Key,
  trendKey: Key,
  label: Name,
  retired: z.boolean(),
  revision: Count,
  modelKey: Ref,
  subtypeKey: Ref,
  divisionCode: BusinessCode.nullable().optional(),
})
const SeriesDecision = z.strictObject({
  ...row,
  classKey: Key,
  seriesKey: Key,
  identityKey: Key.nullable(),
  reviewedDivisionCode: BusinessCode.nullable().optional(),
  revision: Count,
})
const PriorEdition = z.strictObject({
  ...row,
  organizationKey: Key,
  name: Name.optional(),
  year: Count.optional(),
  classes: z.array(
    z.strictObject({
      key: Key,
      origin: Origin.optional(),
      originAliases: z.array(Origin).optional(),
      code: BusinessCode.optional(),
    })
  ),
})
const EditionMapping = z.strictObject({
  ...row,
  classKey: Key,
  previousShowKey: Key,
  previousClassKey: Key.nullable(),
})
const Show = z.strictObject({
  ...row,
  organizationKey: Key,
  name: Name,
  slug: Slug.optional(),
  startDate: Time,
  endDate: Time,
  location: NullableText,
  hostState: HostState.nullish(),
  status: z.enum(['draft', 'accepting_entries', 'judging', 'finalized']),
  previousShowKey: Ref,
  rulesVersion: NullableText,
  pointsConfig: ShowPackagePointsConfig.optional(),
  awardsPolicy: AwardsPolicy.nullable().optional(),
  enableClubs: z.boolean().optional(),
  labelOnlyMode: z.boolean().optional(),
  labelPrintingDisabled: z.boolean().optional(),
  allowStaffEntries: z.boolean().optional(),
  staffMayExhibit: z.boolean().optional(),
  reserveGrandEnabled: z.boolean().optional(),
  defaultPlaceCount: ShowPlaceCount.optional(),
  judgingFinalizedAt: NullableTime,
  seriesMembership: z
    .strictObject({ seriesKey: Key, year: z.number().int().min(1000).max(9999) })
    .nullable()
    .optional(),
  allocation: z
    .strictObject({ algorithm: z.literal('honeyshow-numeric.v1'), seed: Key })
    .optional(),
})
const collections = {
  divisions: z.array(Division),
  classes: z.array(Class),
  models: z.array(Model),
  subtypes: z.array(Subtype),
  ruleSnapshots: z.array(RuleSnapshot),
  entrants: z.array(Entrant),
  entries: z.array(Entry),
  exhibits: z.array(Exhibit),
  identifiers: z.array(Identifier),
  tombstones: z.array(Tombstone),
  scores: z.array(Score),
  awards: z.array(Award),
  series: z.array(Series),
  seriesIdentities: z.array(SeriesIdentity),
  seriesDecisions: z.array(SeriesDecision),
  priorEditions: z.array(PriorEdition),
  editionMappings: z.array(EditionMapping),
}
const profileCollections = (full: boolean) => ({
  organizations: z.array(
    z.strictObject({
      ...row,
      name: Name,
      abbreviation: NullableText,
      website: Text.nullable().optional(),
      ...(full ? { contact: NullableText } : {}),
    })
  ),
  people: z.array(
    z
      .strictObject({
        ...row,
        name: Name,
        firstName: NullableText,
        lastName: NullableText,
        contactCorrectionFields: z.array(ContactField).optional(),
        ahstcMember: z.boolean().optional(),
        ahstcJudge: z.boolean().optional(),
        credentialNumber: NullableText,
        judgeCertificationLevel: NullableText,
        judgeStatus: NullableText,
        judgeTrainingDate: NullableTime,
        memberId: NullableText,
        membershipExpiresAt: NullableTime,
        ...(full ? { contact: Contact.optional() } : {}),
      })
      .superRefine((person, context) => {
        for (const field of person.contactCorrectionFields ?? []) {
          const included = ['name', 'firstName', 'lastName'].includes(field)
            ? Object.hasOwn(person, field)
            : full &&
              Object.hasOwn((person as { contact?: Record<string, unknown> }).contact ?? {}, field)
          if (!included)
            context.addIssue({
              code: 'custom',
              path: ['contactCorrectionFields'],
              message: 'Correction metadata requires its contact value in this privacy profile',
            })
        }
      })
  ),
  exhibitors: z.array(
    z.strictObject({
      ...row,
      name: Name,
      personKey: Ref,
      organizationKey: Ref,
      affiliation: NullableText,
      entrantCode: z
        .string()
        .regex(/^[A-Z0-9-]{1,6}$/)
        .nullable()
        .optional(),
      ...(full ? { contact: Contact.pick({ email: true, phone: true }).optional() } : {}),
    })
  ),
  staff: z.array(
    z.strictObject({
      ...row,
      role: z.enum([
        'organizer',
        'presiding_judge',
        'lead_secretary',
        'secretary',
        'steward',
        'judge',
        'viewer',
      ]),
      personKey: Ref,
      name: NullableText,
      ...(full ? { contact: NullableText } : {}),
    })
  ),
  clubs: z.array(z.strictObject({ ...row, name: Name, organizationKey: Ref })),
})
export const SHOW_PACKAGE_COLLECTIONS = [
  'organizations',
  'people',
  'exhibitors',
  'staff',
  'clubs',
  'divisions',
  'classes',
  'models',
  'subtypes',
  'ruleSnapshots',
  'entrants',
  'entries',
  'exhibits',
  'identifiers',
  'tombstones',
  'scores',
  'awards',
  'series',
  'seriesIdentities',
  'seriesDecisions',
  'priorEditions',
  'editionMappings',
] as const
const RecordType = z.enum(SHOW_PACKAGE_COLLECTIONS)
const envelope = {
  schemaVersion: z.literal('honeyshow-show-package.v1'),
  packageKey: Key,
  revision: Count.min(1),
  createdAt: Time,
  purpose: z.enum(['seed', 'snapshot', 'handover']),
  originInstallationKey: Key.optional(),
  previous: z.strictObject({ revision: Count.min(1), contentDigest: Digest }).optional(),
  contentDigest: Digest,
  handover: z
    .strictObject({
      transferKey: Key,
      fromInstallationKey: Key,
      toInstallationKey: Key,
      frozenAt: Time,
      intakeDevicesSynced: z.literal(true),
    })
    .optional(),
  removals: z.array(z.strictObject({ type: RecordType, key: Key })),
}
function profile(full: boolean) {
  const all = { ...profileCollections(full), ...collections }
  return z.strictObject({
    ...envelope,
    privacyProfile: z.literal(full ? 'full-contact' : 'limited'),
    show: Show,
    records: z.strictObject(all).partial().required({ organizations: true }),
  })
}
/** Structural schema: the source for generated JSON Schema. Does not verify graph/digest. */
export const ShowPackage = z.discriminatedUnion('privacyProfile', [profile(false), profile(true)])
export type TShowPackage = z.infer<typeof ShowPackage>
export type ShowPackageDiagnostic = {
  code:
    | 'structure'
    | 'version'
    | 'reference'
    | 'duplicate'
    | 'invariant'
    | 'privacy'
    | 'digest'
    | 'baseline'
    | 'missing_data'
  path: string
  message: string
}
export type ShowPackageValidation =
  | { valid: true; package: TShowPackage; diagnostics: [] }
  | { valid: false; diagnostics: ShowPackageDiagnostic[] }

// Diagnostic paths contain only schema-owned property names and array indexes.
const safeFields = new Set<string>()
function collectFields(value: unknown) {
  if (!value || typeof value !== 'object') return
  const node = value as Record<string, unknown>
  if (node.properties && typeof node.properties === 'object')
    Object.keys(node.properties).forEach(key => safeFields.add(key))
  Object.values(node).forEach(child => {
    if (Array.isArray(child)) child.forEach(collectFields)
    else if (child && typeof child === 'object') collectFields(child)
  })
}
export function showPackageJsonSchema() {
  return z.toJSONSchema(ShowPackage, { target: 'draft-2020-12', reused: 'ref' })
}
function safePath(path: PropertyKey[]) {
  if (safeFields.size === 0) collectFields(showPackageJsonSchema())
  return (
    '/' +
    path
      .map(part =>
        typeof part === 'number' ? String(part) : safeFields.has(String(part)) ? String(part) : '*'
      )
      .join('/')
  )
}
const compare = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)
/** Object keys use UTF-16 order; numbers use ECMAScript JSON serialization, including fractions. */
export function canonicalShowPackageJson(value: unknown): string {
  if (value === null || typeof value === 'string' || typeof value === 'boolean')
    return JSON.stringify(value)
  if (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    Math.abs(value) <= Number.MAX_SAFE_INTEGER
  )
    return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(canonicalShowPackageJson).join(',')}]`
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const record = value as Record<string, unknown>
    return `{${Object.keys(record)
      .filter(key => record[key] !== undefined)
      .sort(compare)
      .map(key => `${JSON.stringify(key)}:${canonicalShowPackageJson(record[key])}`)
      .join(',')}}`
  }
  throw new Error('Expected finite JSON data')
}
function sortOriginAliases<T extends { originAliases?: z.infer<typeof Origin>[] }>(row: T): T {
  return row.originAliases === undefined
    ? row
    : {
        ...row,
        originAliases: [...row.originAliases].sort(
          (a, b) => compare(a.namespace, b.namespace) || compare(a.recordId, b.recordId)
        ),
      }
}
export function showPackageDigestInput(pkg: TShowPackage): string {
  const { contentDigest: _digest, ...content } = pkg
  const records = Object.fromEntries(
    Object.entries(content.records)
      .filter(([, rows]) => rows !== undefined)
      .map(([name, rows]) => [
        name,
        [...rows]
          .map(row =>
            name === 'priorEditions'
              ? {
                  ...sortOriginAliases(row),
                  classes: [...(row as z.infer<typeof PriorEdition>).classes]
                    .map(sortOriginAliases)
                    .sort((a, b) => compare(a.key, b.key)),
                }
              : sortOriginAliases(row)
          )
          .sort((a, b) => compare(a.key, b.key)),
      ])
  )
  return canonicalShowPackageJson({
    ...content,
    show: sortOriginAliases(content.show),
    records,
    removals: [...content.removals].sort((a, b) =>
      compare(`${a.type}:${a.key}`, `${b.type}:${b.key}`)
    ),
  })
}
export async function computeShowPackageDigest(pkg: TShowPackage): Promise<string> {
  const bytes = new TextEncoder().encode(showPackageDigestInput(pkg))
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  return (
    'sha256:' +
    Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
  )
}

export async function validateShowPackage(
  input: unknown,
  options: { baseline?: unknown; now?: Date } = {}
): Promise<ShowPackageValidation> {
  if (
    Array.isArray(options.baseline) &&
    (options.baseline.length === 0 || options.baseline.length > 64)
  )
    return {
      valid: false,
      diagnostics: [
        {
          code: 'baseline',
          path: '/previous',
          message: 'Supply one to 64 baseline packages in oldest-to-newest order.',
        },
      ],
    }
  const result = await validatePackage(input, options, false)
  return result.valid ? { valid: true, package: result.package, diagnostics: [] } : result
}
/** Omitted fields retain known source data; arrays and explicit null replace it. No destination writes. */
function mergeSuppliedPackageFields(previous: unknown, supplied: unknown): unknown {
  if (
    previous &&
    supplied &&
    typeof previous === 'object' &&
    typeof supplied === 'object' &&
    !Array.isArray(previous) &&
    !Array.isArray(supplied)
  ) {
    const merged: Record<string, unknown> = { ...previous }
    for (const [field, value] of Object.entries(supplied))
      merged[field] = mergeSuppliedPackageFields(merged[field], value)
    return merged
  }
  return supplied
}

async function validatePackage(
  input: unknown,
  options: { baseline?: unknown; now?: Date },
  baselineOnly: boolean
): Promise<ShowPackageValidation & { resolvedSource?: TShowPackage }> {
  const parsed = ShowPackage.safeParse(input)
  if (!parsed.success) {
    const version =
      input &&
      typeof input === 'object' &&
      'schemaVersion' in input &&
      input.schemaVersion !== 'honeyshow-show-package.v1'
    return {
      valid: false,
      diagnostics: parsed.error.issues.slice(0, 100).map(issue => ({
        code: version ? 'version' : issue.code === 'unrecognized_keys' ? 'privacy' : 'structure',
        path: safePath(issue.path),
        message:
          issue.code === 'unrecognized_keys'
            ? 'Unsupported field; remove or review against the allowlist.'
            : 'Value does not match the package contract.',
      })),
    }
  }
  const pkg = JSON.parse(canonicalShowPackageJson(parsed.data)) as TShowPackage
  let resolvedSource: TShowPackage | undefined
  const diagnostics: ShowPackageDiagnostic[] = []
  const problem = (code: ShowPackageDiagnostic['code'], path: string, message: string) => {
    if (diagnostics.length < 100) diagnostics.push({ code, path, message })
  }
  const linkedCorrection = Boolean(pkg.previous && options.baseline && !baselineOnly)
  const tables = pkg.records
  type Row = {
    key: string
    origin?: z.infer<typeof Origin>
    originAliases?: z.infer<typeof Origin>[]
  }
  const maps = new Map<string, Map<string, Row>>()
  for (const [type, rows] of Object.entries(tables)) {
    const map = new Map<string, Row>()
    const origins = new Set<string>()
    rows.forEach((record, i) => {
      if (map.has(record.key))
        problem(
          'duplicate',
          `/records/${type}/${i}/key`,
          'Record key must be unique within its type.'
        )
      map.set(record.key, record)
      const aliases = record.originAliases ?? []
      const own = new Set(aliases.map(canonicalShowPackageJson))
      if (own.size !== aliases.length)
        problem(
          'duplicate',
          `/records/${type}/${i}/originAliases`,
          'Origin aliases must be unique.'
        )
      if (record.origin) own.add(canonicalShowPackageJson(record.origin))
      for (const source of own) {
        if (origins.has(source))
          problem(
            'duplicate',
            `/records/${type}/${i}/originAliases`,
            'Origin identity must be unique within its type.'
          )
        origins.add(source)
      }
    })
    maps.set(type, map)
  }
  function checkAliasSet(row: { originAliases?: z.infer<typeof Origin>[] }, path: string) {
    if (
      row.originAliases &&
      new Set(row.originAliases.map(canonicalShowPackageJson)).size !== row.originAliases.length
    )
      problem('duplicate', path, 'Origin aliases must be unique.')
  }
  checkAliasSet(pkg.show, '/show/originAliases')
  const classOrigins = new Set(
    (tables.classes ?? []).flatMap(row =>
      [...(row.originAliases ?? []), ...(row.origin ? [row.origin] : [])].map(
        canonicalShowPackageJson
      )
    )
  )
  for (const [i, edition] of (tables.priorEditions ?? []).entries()) {
    const origins = classOrigins
    for (const [j, klass] of edition.classes.entries()) {
      const path = `/records/priorEditions/${i}/classes/${j}/originAliases`
      checkAliasSet(klass, path)
      for (const alias of new Set(
        [...(klass.originAliases ?? []), ...(klass.origin ? [klass.origin] : [])].map(
          canonicalShowPackageJson
        )
      )) {
        if (origins.has(alias))
          problem('duplicate', path, 'Origin identity must be unique within its type.')
        origins.add(alias)
      }
    }
  }
  function ref(type: string, key: string | null | undefined, path: string) {
    if (key != null && !maps.get(type)?.has(key))
      problem('reference', path, 'Referenced record is missing from the package.')
  }
  function unique(values: (string | null | undefined)[], path: string) {
    const seen = new Set<string>()
    values.forEach((value, i) => {
      if (value == null) return
      if (seen.has(value)) problem('duplicate', `${path}/${i}`, 'Business identity must be unique.')
      seen.add(value)
    })
  }
  if (!linkedCorrection) {
    if (new Date(pkg.show.startDate) > new Date(pkg.show.endDate))
      problem('invariant', '/show/endDate', 'Show end precedes its start.')
    if (pkg.show.status !== 'finalized' && pkg.show.judgingFinalizedAt)
      problem(
        'invariant',
        '/show/judgingFinalizedAt',
        'A finalization timestamp requires finalized show status.'
      )
    ref('organizations', pkg.show.organizationKey, '/show/organizationKey')
    ref('priorEditions', pkg.show.previousShowKey, '/show/previousShowKey')
  }
  if (pkg.purpose !== 'seed') {
    const requireFields = (value: object, fields: readonly string[], path: string) => {
      for (const field of fields)
        if (!Object.prototype.hasOwnProperty.call(value, field))
          problem(
            'missing_data',
            `${path}/${field}`,
            'Server snapshots require known operational settings; use explicit null when no value is set.'
          )
    }
    requireFields(
      pkg.show,
      [
        'slug',
        'location',
        'rulesVersion',
        'pointsConfig',
        'awardsPolicy',
        'enableClubs',
        'labelOnlyMode',
        'labelPrintingDisabled',
        'allowStaffEntries',
        'staffMayExhibit',
        'reserveGrandEnabled',
        'judgingFinalizedAt',
        'previousShowKey',
        'seriesMembership',
      ],
      '/show'
    )
    const completeFields = {
      organizations: ['abbreviation', 'website'],
      people: [
        'updatedAt',
        'firstName',
        'lastName',
        'ahstcMember',
        'memberId',
        'membershipExpiresAt',
        'ahstcJudge',
        'credentialNumber',
        'judgeCertificationLevel',
        'judgeStatus',
        'judgeTrainingDate',
      ],
      exhibitors: ['personKey', 'organizationKey', 'affiliation', 'entrantCode', 'updatedAt'],
      staff: ['personKey', 'name', 'createdAt'],
      clubs: ['organizationKey', 'createdAt'],
      models: ['description', 'aliases', 'flags', 'updatedAt'],
      subtypes: ['description'],
      scores: ['judgeKey', 'judgeOrigin', 'comment'],
      awards: ['divisionKey', 'notes', 'createdAt'],
      seriesIdentities: ['modelKey', 'subtypeKey', 'divisionCode'],
      seriesDecisions: ['reviewedDivisionCode'],
    } as const
    for (const type of Object.keys(completeFields) as (keyof typeof completeFields)[])
      tables[type]?.forEach((r, i) =>
        requireFields(r, completeFields[type], `/records/${type}/${i}`)
      )
    tables.classes?.forEach((r, i) =>
      requireFields(
        r,
        [
          'createdAt',
          'description',
          'displayName',
          'rules',
          'modelKey',
          'subtypeKey',
          'modelDefaults',
          'modelLinkValidated',
          'maxEntriesPerExhibitor',
          'ruleOverrides',
          'parameters',
          'judgingStartedAt',
          'resultsFinalizedAt',
        ],
        `/records/classes/${i}`
      )
    )
    const requireProvenance = (value: z.infer<typeof Provenance> | undefined, path: string) => {
      if (!value) problem('missing_data', path, 'Server copies require known catalog provenance.')
      else
        requireFields(
          value,
          ['origin', 'sourceInstanceId', 'sourceKey', 'importedDigest', 'publicationVersion'],
          path
        )
    }
    for (const type of ['models', 'subtypes'] as const)
      tables[type]?.forEach((r, i) =>
        requireProvenance(r.provenance, `/records/${type}/${i}/provenance`)
      )
    tables.classes?.forEach((r, i) => {
      if (!r.modelDefaults) return
      const path = `/records/classes/${i}/modelDefaults`
      requireFields(
        r.modelDefaults,
        ['capturedAt', 'adoptedAt', 'adoptedBy', 'modelKey', 'subtypeKey', 'subtypeSnapshot'],
        path
      )
      if (r.modelDefaults.modelKey) {
        requireFields(r.modelDefaults, ['modelRevision'], path)
        requireProvenance(r.modelDefaults.modelProvenance, `${path}/modelProvenance`)
      }
      if (r.modelDefaults.subtypeKey) {
        requireFields(r.modelDefaults, ['subtypeRevision'], path)
        if (!r.modelDefaults.subtypeSnapshot)
          problem(
            'missing_data',
            `${path}/subtypeSnapshot`,
            'A captured subtype requires its saved defaults.'
          )
        else
          requireProvenance(
            r.modelDefaults.subtypeSnapshot.provenance,
            `${path}/subtypeSnapshot/provenance`
          )
      }
    })
    tables.entries?.forEach((r, i) =>
      requireFields(
        r,
        [
          'submission',
          'createdAt',
          'updatedAt',
          'clubKey',
          'clubName',
          'title',
          'notes',
          'overrideDuplicateReason',
          'overrideDuplicateBy',
          'overrideDuplicateAt',
          'notEnteredAt',
          'notEnteredBy',
          'notEnteredByRole',
          'notEnteredReason',
        ],
        `/records/entries/${i}`
      )
    )
    tables.ruleSnapshots?.forEach((r, i) =>
      requireFields(
        r,
        [
          'createdAt',
          'sourceChecksum',
          'sourceType',
          'sourceId',
          'rulesVersion',
          'title',
          'approvedAt',
          'approvedBy',
          'createdBy',
        ],
        `/records/ruleSnapshots/${i}`
      )
    )
    tables.entrants?.forEach((r, i) =>
      requireFields(
        r,
        ['clubKey', 'clubName', 'finalizedAt', 'finalizedBy', 'finalizedByRole', 'updatedAt'],
        `/records/entrants/${i}`
      )
    )
    tables.exhibits?.forEach((r, i) =>
      requireFields(r, ['stagingLocation'], `/records/exhibits/${i}`)
    )
    for (const name of SHOW_PACKAGE_COLLECTIONS)
      if (!(name in tables))
        problem(
          'missing_data',
          `/records/${name}`,
          'Snapshots and handovers require every collection, including known empty collections.'
        )
    if (!pkg.show.allocation)
      problem(
        'missing_data',
        '/show/allocation',
        'Snapshots and handovers require the effective allocator seed.'
      )
  }
  if ((pkg.purpose === 'handover') !== Boolean(pkg.handover))
    problem('invariant', '/handover', 'Handover metadata is required only for handover packages.')
  if (
    pkg.handover &&
    (pkg.handover.fromInstallationKey !== pkg.originInstallationKey ||
      pkg.handover.fromInstallationKey === pkg.handover.toInstallationKey)
  )
    problem('invariant', '/handover', 'Handover origin and distinct destination must agree.')
  if (pkg.purpose !== 'seed' && !pkg.originInstallationKey)
    problem(
      'missing_data',
      '/originInstallationKey',
      'Server snapshots require an originating installation.'
    )
  if ((pkg.revision === 1 && pkg.previous) || (pkg.revision > 1 && !pkg.previous))
    problem(
      'baseline',
      '/previous',
      'Corrections require a preceding revision; initial packages must not declare one.'
    )
  if (pkg.previous && pkg.previous.revision !== pkg.revision - 1)
    problem('baseline', '/previous/revision', 'Corrections must follow the preceding revision.')
  if (pkg.removals.length && !pkg.previous)
    problem('baseline', '/removals', 'Removals require a baseline.')
  unique(
    pkg.removals.map(r => `${r.type}:${r.key}`),
    '/removals'
  )
  pkg.removals.forEach((r, i) => {
    if (maps.get(r.type)?.has(r.key))
      problem('invariant', `/removals/${i}`, 'Removed records cannot also be supplied.')
  })
  // Linked corrections validate relationships once, against the retained source graph below.
  if (!linkedCorrection) {
    for (const type of ['models', 'subtypes'] as const)
      unique(
        tables[type]?.map(r =>
          r.provenance?.sourceInstanceId != null && r.provenance?.sourceKey != null
            ? JSON.stringify([r.provenance.sourceInstanceId, r.provenance.sourceKey])
            : null
        ) ?? [],
        `/records/${type}/provenance`
      )
    const categoryNames = new Map<string, string>()
    tables.models?.forEach((r, i) => {
      if (categoryNames.has(r.categoryCode) && categoryNames.get(r.categoryCode) !== r.categoryName)
        problem(
          'invariant',
          `/records/models/${i}/categoryName`,
          'A category code must have one current category name.'
        )
      categoryNames.set(r.categoryCode, r.categoryName)
    })
    unique(tables.people?.map(r => r.credentialNumber) ?? [], '/records/people/credentialNumber')
    unique(tables.divisions?.map(r => r.code) ?? [], '/records/divisions')
    unique(
      tables.classes?.map(r => JSON.stringify([r.divisionKey, r.code])) ?? [],
      '/records/classes'
    )
    tables.subtypes?.forEach((r, i) => ref('models', r.modelKey, `/records/subtypes/${i}/modelKey`))
    const subtypes = new Map(tables.subtypes?.map(r => [r.key, r]))
    const classes = new Map(tables.classes?.map(r => [r.key, r]))
    function modelRefs(r: { modelKey?: string | null; subtypeKey?: string | null }, path: string) {
      ref('models', r.modelKey, `${path}/modelKey`)
      ref('subtypes', r.subtypeKey, `${path}/subtypeKey`)
      if (r.subtypeKey && subtypes.get(r.subtypeKey)?.modelKey !== r.modelKey)
        problem('invariant', `${path}/subtypeKey`, 'Subtype must belong to the referenced model.')
    }
    tables.classes?.forEach((r, i) => {
      const p = `/records/classes/${i}`
      ref('divisions', r.divisionKey, `${p}/divisionKey`)
      const judgeKeys = r.parameters?.judging?.judgeAssignmentKeys ?? []
      unique(judgeKeys, `${p}/parameters/judging/judgeAssignmentKeys`)
      judgeKeys.forEach((key, j) => {
        const path = `${p}/parameters/judging/judgeAssignmentKeys/${j}`
        ref('staff', key, path)
        const assignment = tables.staff?.find(staff => staff.key === key)
        if (assignment && assignment.role !== 'judge')
          problem('invariant', path, 'Judge assignment must reference staff with the judge role.')
      })
      modelRefs(r, p)
      if (r.modelDefaults) {
        const snapshot = r.modelDefaults
        if (snapshot.kind === 'adopted') modelRefs(snapshot, `${p}/modelDefaults`)
        else {
          ref('models', snapshot.modelKey, `${p}/modelDefaults/modelKey`)
          ref('subtypes', snapshot.subtypeKey, `${p}/modelDefaults/subtypeKey`)
        }
        if (snapshot.subtypeSnapshot?.modelKey) {
          ref(
            'models',
            snapshot.subtypeSnapshot.modelKey,
            `${p}/modelDefaults/subtypeSnapshot/modelKey`
          )
          if (!snapshot.subtypeKey)
            problem(
              'invariant',
              `${p}/modelDefaults/subtypeSnapshot/modelKey`,
              'A saved subtype parent requires a saved subtype.'
            )
          else if (
            snapshot.kind === 'adopted' &&
            snapshot.subtypeSnapshot.modelKey !== snapshot.modelKey
          )
            problem(
              'invariant',
              `${p}/modelDefaults/subtypeSnapshot/modelKey`,
              'Adopted subtype parent must match the snapshot model.'
            )
        }
        // Reviewed legacy relinking preserves the baseline captured before reconciliation.
        if (
          r.modelDefaults.kind === 'adopted' &&
          (r.modelDefaults.modelKey !== r.modelKey || r.modelDefaults.subtypeKey !== r.subtypeKey)
        )
          problem(
            'invariant',
            `${p}/modelDefaults`,
            'Snapshot identity must match the class model identity.'
          )
      }
    })
    tables.ruleSnapshots?.forEach((r, i) =>
      r.snapshot.sections?.forEach((s, j) =>
        s.blocks.forEach((b, k) =>
          b.classKeys?.forEach((key, l) =>
            ref(
              'classes',
              key,
              `/records/ruleSnapshots/${i}/snapshot/sections/${j}/blocks/${k}/classKeys/${l}`
            )
          )
        )
      )
    )
    unique(
      tables.exhibitors?.map(r =>
        r.organizationKey != null && r.entrantCode != null
          ? JSON.stringify([r.organizationKey, r.entrantCode.trim().toUpperCase()])
          : null
      ) ?? [],
      '/records/exhibitors'
    )
    unique(
      tables.clubs?.map(r =>
        r.organizationKey != null
          ? JSON.stringify([r.organizationKey, r.name.trim().toLocaleLowerCase('en-US')])
          : null
      ) ?? [],
      '/records/clubs'
    )
    tables.exhibitors?.forEach((r, i) => {
      ref('people', r.personKey, `/records/exhibitors/${i}/personKey`)
      ref('organizations', r.organizationKey, `/records/exhibitors/${i}/organizationKey`)
      if (r.organizationKey && r.organizationKey !== pkg.show.organizationKey)
        problem(
          'invariant',
          `/records/exhibitors/${i}/organizationKey`,
          'Exhibitor must belong to the show organization or be unscoped.'
        )
    })
    tables.staff?.forEach((r, i) => {
      ref('people', r.personKey, `/records/staff/${i}/personKey`)
      if (!r.personKey && !r.name)
        problem(
          'missing_data',
          `/records/staff/${i}`,
          'Staff require a person reference or a supplied name.'
        )
    })
    tables.clubs?.forEach((r, i) =>
      ref('organizations', r.organizationKey, `/records/clubs/${i}/organizationKey`)
    )
    const clubs = new Map(tables.clubs?.map(r => [r.key, r]))
    function clubScope(clubKey: string | null | undefined, path: string) {
      const organization = clubKey ? clubs.get(clubKey)?.organizationKey : null
      if (organization && organization !== pkg.show.organizationKey)
        problem(
          'invariant',
          path,
          'Referenced club must belong to the show organization or be unscoped.'
        )
    }
    const entrants = new Map(tables.entrants?.map(r => [r.exhibitorKey, r]))
    unique(tables.entrants?.map(r => r.exhibitorKey) ?? [], '/records/entrants')
    tables.entrants?.forEach((r, i) => {
      ref('exhibitors', r.exhibitorKey, `/records/entrants/${i}/exhibitorKey`)
      ref('clubs', r.clubKey, `/records/entrants/${i}/clubKey`)
      clubScope(r.clubKey, `/records/entrants/${i}/clubKey`)
    })
    const entries = new Map(tables.entries?.map(r => [r.key, r]))
    unique(tables.entries?.map(r => r.exhibitId) ?? [], '/records/entries')
    unique(
      [
        ...(tables.entries?.map(r => r.submission?.id) ?? []),
        ...(tables.tombstones?.map(r => r.submissionId) ?? []),
      ],
      '/records/tombstones'
    )
    tables.entries?.forEach((r, i) => {
      const p = `/records/entries/${i}`
      const retryRefs = r.submission?.fingerprint?.references
      if (retryRefs) {
        ref('classes', retryRefs.classKey, `${p}/submission/fingerprint/references/classKey`)
        ref(
          'exhibitors',
          retryRefs.exhibitorKey,
          `${p}/submission/fingerprint/references/exhibitorKey`
        )
        ref('clubs', retryRefs.clubKey, `${p}/submission/fingerprint/references/clubKey`)
        clubScope(retryRefs.clubKey, `${p}/submission/fingerprint/references/clubKey`)
      }
      ref('classes', r.classKey, `${p}/classKey`)
      ref('exhibitors', r.exhibitorKey, `${p}/exhibitorKey`)
      ref('clubs', r.clubKey, `${p}/clubKey`)
      clubScope(r.clubKey, `${p}/clubKey`)
      if (!entrants.has(r.exhibitorKey))
        problem('reference', `${p}/exhibitorKey`, 'Entry exhibitor must be a show entrant.')
    })
    tables.tombstones?.forEach((r, i) => {
      if (entries.has(r.deletedEntryKey))
        problem(
          'invariant',
          `/records/tombstones/${i}/deletedEntryKey`,
          'A deleted entry cannot also be live.'
        )
    })
    const exhibits = new Map(tables.exhibits?.map(r => [r.key, r]))
    unique(tables.exhibits?.map(r => r.entryKey) ?? [], '/records/exhibits')
    tables.exhibits?.forEach((r, i) =>
      ref('entries', r.entryKey, `/records/exhibits/${i}/entryKey`)
    )
    unique(tables.identifiers?.map(r => r.value) ?? [], '/records/identifiers')
    unique(tables.identifiers?.map(r => String(r.orderIndex)) ?? [], '/records/identifiers')
    unique(tables.identifiers?.map(r => r.assignedEntryKey) ?? [], '/records/identifiers')
    const assigned = new Set<string>()
    tables.identifiers?.forEach((r, i) => {
      const p = `/records/identifiers/${i}`
      ref('entries', r.assignedEntryKey, `${p}/assignedEntryKey`)
      ref('exhibitors', r.assignedExhibitorKey, `${p}/assignedExhibitorKey`)
      if (r.kind === 'historical') {
        if (
          !(
            (r.source === 'historical_import' && r.assignedEntryKey) ||
            (r.source === 'void' && !r.assignedEntryKey && !r.assignedExhibitorKey && !r.assignedAt)
          ) ||
          r.orderIndex !== historicalIdentifierOrder(r.value) ||
          new Date(pkg.show.endDate) >= (options.now ?? new Date())
        )
          problem(
            'invariant',
            p,
            'Historical identifiers require a past show and their dedicated index, with an imported entry or cleared void assignment.'
          )
      } else if (
        !/^[1-9][0-9]{3}$/.test(r.value) ||
        r.orderIndex > 8999 ||
        r.source === 'historical_import'
      )
        problem(
          'invariant',
          p,
          'Ordinary identifiers must use 1000–9999 and allocation indexes 0–8999.'
        )
      if (!r.assignedEntryKey && r.source === null && (r.assignedExhibitorKey || r.assignedAt))
        problem('invariant', p, 'Free inventory cannot contain assignments.')
      if (r.assignedEntryKey && (!r.assignedAt || !r.assignedExhibitorKey))
        problem('invariant', p, 'Assigned entries require assignment time and entrant.')
      if (r.source === 'void' && (r.assignedEntryKey || r.assignedExhibitorKey || r.assignedAt))
        problem('invariant', p, 'Retired identifiers must have every assignment field cleared.')
      if (r.assignedEntryKey) {
        const e = entries.get(r.assignedEntryKey)
        assigned.add(r.assignedEntryKey)
        if (e && (e.exhibitId !== r.value || e.exhibitorKey !== r.assignedExhibitorKey))
          problem('invariant', p, 'Identifier value and exhibitor must match its entry.')
      }
    })
    tables.entries?.forEach((r, i) => {
      if (!assigned.has(r.key))
        problem(
          'reference',
          `/records/entries/${i}/exhibitId`,
          'Entry requires its exact identifier assignment.'
        )
    })
    const exhibitEntries = new Set(tables.exhibits?.map(r => r.entryKey))
    tables.entries?.forEach((r, i) => {
      if (
        (['checked_in', 'judged', 'finalized'].includes(r.status) && !exhibitEntries.has(r.key)) ||
        (r.status === 'draft' && exhibitEntries.has(r.key))
      )
        problem(
          'invariant',
          `/records/entries/${i}/status`,
          'Checked-in or judged entries require an exhibit; draft entries must not have one.'
        )
    })
    tables.scores?.forEach((r, i) => {
      ref('exhibits', r.exhibitKey, `/records/scores/${i}/exhibitKey`)
      ref('people', r.judgeKey, `/records/scores/${i}/judgeKey`)
      if (r.judgeKey && r.judgeOrigin)
        problem(
          'invariant',
          `/records/scores/${i}/judgeOrigin`,
          'Use either a linked person or an opaque source judge identity.'
        )
    })
    if ((tables.awards?.filter(award => award.type === 'hero_award').length ?? 0) > 1)
      problem('invariant', '/records/awards', 'Only one Hero Award may be recorded.')
    tables.awards?.forEach((r, i) => {
      const p = `/records/awards/${i}`
      ref('exhibits', r.exhibitKey, `${p}/exhibitKey`)
      ref('entrants', r.entrantKey, `${p}/entrantKey`)
      ref('divisions', r.divisionKey, `${p}/divisionKey`)
      if (r.type === 'hero_award' && !r.exhibitKey)
        problem('invariant', p, 'Hero Award requires an exhibit subject.')
      if (r.type === 'hero_award' && (r.divisionKey != null || r.place !== '1st'))
        problem('invariant', p, 'Hero Award is a show-wide selection with canonical place 1st.')
      if (Boolean(r.exhibitKey) === Boolean(r.entrantKey))
        problem('invariant', p, 'Award requires exactly one exhibit or entrant subject.')
      if (r.exhibitKey && r.divisionKey) {
        const entry = entries.get(exhibits.get(r.exhibitKey)?.entryKey ?? '')
        if (entry && classes.get(entry.classKey)?.divisionKey !== r.divisionKey)
          problem('invariant', p, 'Award division must match its exhibit.')
      }
    })
    tables.series?.forEach((r, i) =>
      ref('organizations', r.organizationKey, `/records/series/${i}/organizationKey`)
    )
    const membership = pkg.show.seriesMembership
    const showYear = new Date(pkg.show.startDate).getUTCFullYear()
    if (membership) {
      if (membership.year !== showYear)
        problem(
          'invariant',
          '/show/seriesMembership/year',
          'Series edition year must match the show start UTC year.'
        )
      ref('series', membership.seriesKey, '/show/seriesMembership/seriesKey')
      if (
        tables.series?.find(r => r.key === membership.seriesKey)?.organizationKey !==
        pkg.show.organizationKey
      )
        problem(
          'invariant',
          '/show/seriesMembership',
          'Series and show must belong to the same organization.'
        )
    }
    unique(tables.seriesIdentities?.map(r => r.trendKey) ?? [], '/records/seriesIdentities')
    tables.seriesIdentities?.forEach((r, i) => {
      ref('series', r.seriesKey, `/records/seriesIdentities/${i}/seriesKey`)
      modelRefs(r, `/records/seriesIdentities/${i}`)
    })
    unique(tables.seriesDecisions?.map(r => r.classKey) ?? [], '/records/seriesDecisions')
    unique(tables.seriesDecisions?.map(r => r.identityKey) ?? [], '/records/seriesDecisions')
    tables.seriesDecisions?.forEach((r, i) => {
      const p = `/records/seriesDecisions/${i}`
      const klass = classes.get(r.classKey)
      const identity = tables.seriesIdentities?.find(identity => identity.key === r.identityKey)
      if (klass && identity?.modelKey) {
        if (
          klass.modelKey !== identity.modelKey ||
          (klass.subtypeKey ?? null) !== (identity.subtypeKey ?? null)
        )
          problem(
            'invariant',
            `${p}/identityKey`,
            'Series class assignment must use the same model and subtype.'
          )
        const divisionCode = tables.divisions?.find(
          division => division.key === klass.divisionKey
        )?.code
        if (identity.divisionCode !== divisionCode && r.reviewedDivisionCode !== divisionCode)
          problem(
            'invariant',
            `${p}/reviewedDivisionCode`,
            'A division difference requires explicit acknowledgment of the class division code.'
          )
      }
      ref('classes', r.classKey, `${p}/classKey`)
      ref('seriesIdentities', r.identityKey, `${p}/identityKey`)
      if (
        !membership ||
        membership.seriesKey !== r.seriesKey ||
        (r.identityKey &&
          tables.seriesIdentities?.find(s => s.key === r.identityKey)?.seriesKey !== r.seriesKey)
      )
        problem('invariant', p, 'Alignment must belong to the show series.')
    })
    tables.priorEditions?.forEach((r, i) => {
      if (
        r.key === pkg.show.key ||
        r.organizationKey !== pkg.show.organizationKey ||
        [...(r.originAliases ?? []), ...(r.origin ? [r.origin] : [])].some(alias =>
          [...(pkg.show.originAliases ?? []), ...(pkg.show.origin ? [pkg.show.origin] : [])].some(
            showAlias => canonicalShowPackageJson(showAlias) === canonicalShowPackageJson(alias)
          )
        )
      )
        problem(
          'invariant',
          `/records/priorEditions/${i}`,
          'Prior edition must be a distinct show in the same organization.'
        )
      if (r.year === undefined)
        problem(
          'missing_data',
          `/records/priorEditions/${i}/year`,
          'Prior edition year is required to verify chronology.'
        )
      else if (r.year >= showYear)
        problem(
          'invariant',
          `/records/priorEditions/${i}/year`,
          'Prior edition must precede the current show UTC year.'
        )
      ref('organizations', r.organizationKey, `/records/priorEditions/${i}/organizationKey`)
      unique(
        r.classes.map(c => c.key),
        `/records/priorEditions/${i}/classes`
      )
    })
    unique(tables.editionMappings?.map(r => r.classKey) ?? [], '/records/editionMappings')
    unique(
      tables.editionMappings?.map(r =>
        r.previousClassKey ? JSON.stringify([r.previousShowKey, r.previousClassKey]) : null
      ) ?? [],
      '/records/editionMappings'
    )
    tables.editionMappings?.forEach((r, i) => {
      const p = `/records/editionMappings/${i}`
      ref('classes', r.classKey, `${p}/classKey`)
      ref('priorEditions', r.previousShowKey, `${p}/previousShowKey`)
      if (r.previousShowKey !== pkg.show.previousShowKey)
        problem('invariant', p, 'Prior-edition mapping must use the show prior edition.')
      if (
        r.previousClassKey &&
        !tables.priorEditions
          ?.find(s => s.key === r.previousShowKey)
          ?.classes.some(c => c.key === r.previousClassKey)
      )
        problem(
          'reference',
          `${p}/previousClassKey`,
          'Previous class must be declared in the referenced prior edition.'
        )
    })
    const required = {
      exhibitors: new Set([
        ...(tables.entrants?.map(r => r.exhibitorKey) ?? []),
        ...(tables.identifiers?.map(r => r.assignedExhibitorKey) ?? []),
        ...(tables.entries?.map(r => r.submission?.fingerprint?.references?.exhibitorKey) ?? []),
      ]),
      clubs: new Set([
        ...(tables.entrants?.map(r => r.clubKey) ?? []),
        ...(tables.entries?.map(r => r.clubKey) ?? []),
        ...(tables.entries?.map(r => r.submission?.fingerprint?.references?.clubKey) ?? []),
      ]),
      series: new Set([membership?.seriesKey]),
      seriesIdentities: new Set(tables.seriesDecisions?.map(r => r.identityKey)),
      priorEditions: new Set([pkg.show.previousShowKey]),
      models: new Set([
        ...(tables.classes?.map(r => r.modelKey) ?? []),
        ...(tables.classes?.map(r => r.modelDefaults?.modelKey) ?? []),
        ...(tables.classes?.map(r => r.modelDefaults?.subtypeSnapshot?.modelKey) ?? []),
        ...(tables.subtypes?.map(r => r.modelKey) ?? []),
        ...(tables.seriesIdentities?.map(r => r.modelKey) ?? []),
      ]),
      subtypes: new Set([
        ...(tables.classes?.map(r => r.subtypeKey) ?? []),
        ...(tables.classes?.map(r => r.modelDefaults?.subtypeKey) ?? []),
        ...(tables.seriesIdentities?.map(r => r.subtypeKey) ?? []),
      ]),
      organizations: new Set([
        pkg.show.organizationKey,
        ...(tables.exhibitors?.map(r => r.organizationKey) ?? []),
        ...(tables.clubs?.map(r => r.organizationKey) ?? []),
        ...(tables.series?.map(r => r.organizationKey) ?? []),
        ...(tables.priorEditions?.map(r => r.organizationKey) ?? []),
      ]),
    }
    for (const [type, keys] of Object.entries(required)) {
      const rows = tables[type as keyof typeof tables]
      rows?.forEach((r, i) => {
        if (!keys.has(r.key))
          problem('privacy', `/records/${type}/${i}`, 'Include only referenced dependency records.')
      })
    }
    // Only people needed by this show's operational graph may travel.
    const peopleUsed = new Set([
      ...(tables.exhibitors?.map(r => r.personKey) ?? []),
      ...(tables.staff?.map(r => r.personKey) ?? []),
      ...(tables.scores?.map(r => r.judgeKey) ?? []),
    ])
    tables.people?.forEach((r, i) => {
      if (!peopleUsed.has(r.key))
        problem('privacy', `/records/people/${i}`, 'Unrelated people must not be included.')
    })
  }
  if ((await computeShowPackageDigest(pkg)) !== pkg.contentDigest)
    problem('digest', '/contentDigest', 'Content digest does not match canonical package bytes.')
  if (pkg.previous && !baselineOnly) {
    if (!options.baseline)
      problem('baseline', '/previous', 'Supply the prior package to certify this correction.')
    else {
      // Every correction baseline needs its lineage; graph validity cannot prove completeness.
      const chain = Array.isArray(options.baseline) ? options.baseline : [options.baseline]
      const baseline = await validatePackage(
        chain[chain.length - 1],
        {
          now: options.now,
          ...(chain.length > 1 ? { baseline: chain.slice(0, -1) } : {}),
        },
        chain.length === 1
      )
      if (!baseline.valid)
        problem(
          'baseline',
          '/previous',
          'Prior package is invalid or incomplete; supply earlier baselines for partial revisions.'
        )
      else {
        if (chain.length === 1 && baseline.package.revision !== 1)
          problem(
            'baseline',
            '/previous',
            'Supply the complete baseline chain starting at revision 1; standalone correction baselines cannot prove retained identities.'
          )
        const prior = baseline.resolvedSource ?? baseline.package
        if (
          prior.packageKey !== pkg.packageKey ||
          prior.show.key !== pkg.show.key ||
          prior.revision !== pkg.previous.revision ||
          baseline.package.contentDigest !== pkg.previous.contentDigest
        )
          problem(
            'baseline',
            '/previous',
            'Prior package identity, revision and digest must match.'
          )
        if (
          prior.show.origin &&
          pkg.show.origin !== undefined &&
          canonicalShowPackageJson(prior.show.origin) !==
            canonicalShowPackageJson(pkg.show.origin ?? null)
        )
          problem('baseline', '/show/origin', 'Established show origin cannot change.')
        if (
          prior.show.allocation &&
          pkg.show.allocation !== undefined &&
          canonicalShowPackageJson(prior.show.allocation) !==
            canonicalShowPackageJson(pkg.show.allocation ?? null)
        )
          problem(
            'baseline',
            '/show/allocation',
            'Established allocator seed cannot change or disappear.'
          )
        function preserveAliases(
          before: { originAliases?: z.infer<typeof Origin>[] },
          after: { originAliases?: z.infer<typeof Origin>[] },
          path: string
        ) {
          if (after.originAliases === undefined) return
          const aliases = new Set(after.originAliases.map(canonicalShowPackageJson))
          if (before.originAliases?.some(alias => !aliases.has(canonicalShowPackageJson(alias))))
            problem('baseline', path, 'Established origin aliases cannot be removed or changed.')
        }
        preserveAliases(prior.show, pkg.show, '/show/originAliases')
        const removed = new Set(pkg.removals.map(r => `${r.type}:${r.key}`))
        for (const [type, rows] of Object.entries(prior.records)) {
          const current = maps.get(type)
          if (!current) continue // omitted collection is unknown, never an implicit removal
          const originKeys = new Map(
            [...current.values()].flatMap(r =>
              [...(r.originAliases ?? []), ...(r.origin ? [r.origin] : [])].map(
                alias => [canonicalShowPackageJson(alias), r.key] as const
              )
            )
          )
          rows.forEach(r => {
            if (
              [...(r.originAliases ?? []), ...(r.origin ? [r.origin] : [])].some(
                alias =>
                  originKeys.has(canonicalShowPackageJson(alias)) &&
                  originKeys.get(canonicalShowPackageJson(alias)) !== r.key
              )
            )
              problem(
                'baseline',
                `/records/${type}`,
                'Established origin identity must retain its portable key.'
              )
            const next = current.get(r.key)
            if (next) preserveAliases(r, next, `/records/${type}/originAliases`)
            if (!next && !removed.has(`${type}:${r.key}`))
              problem(
                'baseline',
                `/records/${type}`,
                'Previously supplied records must retain their keys or have explicit removals.'
              )
            if (
              next &&
              r.origin &&
              next.origin !== undefined &&
              canonicalShowPackageJson(r.origin) !== canonicalShowPackageJson(next.origin ?? null)
            )
              problem('baseline', `/records/${type}`, 'Established record origins cannot change.')
          })
        }
        for (const edition of prior.records.priorEditions ?? []) {
          const next = tables.priorEditions?.find(row => row.key === edition.key)
          if (!next) continue
          const aliases = new Map(
            next.classes.flatMap(klass =>
              [...(klass.originAliases ?? []), ...(klass.origin ? [klass.origin] : [])].map(
                alias => [canonicalShowPackageJson(alias), klass.key] as const
              )
            )
          )
          for (const klass of edition.classes) {
            const current = next.classes.find(row => row.key === klass.key)
            if (!current)
              problem(
                'baseline',
                '/records/priorEditions/classes',
                'Established prior-edition classes must retain their portable keys.'
              )
            if (current) {
              // Nested class arrays replace the baseline; omitted fields cannot inherit identities.
              preserveAliases(
                klass,
                { originAliases: current.originAliases ?? [] },
                '/records/priorEditions/classes/originAliases'
              )
              if (
                klass.origin &&
                canonicalShowPackageJson(klass.origin) !==
                  canonicalShowPackageJson(current.origin ?? null)
              )
                problem(
                  'baseline',
                  '/records/priorEditions/classes/origin',
                  'Established class origins cannot change.'
                )
            }
            if (
              [...(klass.originAliases ?? []), ...(klass.origin ? [klass.origin] : [])].some(
                alias =>
                  aliases.has(canonicalShowPackageJson(alias)) &&
                  aliases.get(canonicalShowPackageJson(alias)) !== klass.key
              )
            )
              problem(
                'baseline',
                '/records/priorEditions/classes/originAliases',
                'Established class aliases cannot move to another key.'
              )
          }
        }
        pkg.removals.forEach((r, i) => {
          if (r.type === 'identifiers' || r.type === 'tombstones')
            problem(
              'baseline',
              `/removals/${i}`,
              'Consumed identifier and retry inventory cannot be removed; preserve or retire the record.'
            )
          if (
            !(prior.records[r.type as keyof typeof prior.records] as Row[] | undefined)?.some(
              old => old.key === r.key
            )
          )
            problem('baseline', `/removals/${i}`, 'Removal does not identify a baseline record.')
        })
        // Check every correction against retained source records, including omitted collections.
        // This is validation only, not destination reconciliation. Privacy never expands the returned package.
        const retainedRecords = Object.fromEntries(
          SHOW_PACKAGE_COLLECTIONS.flatMap(type => {
            const previousRows = prior.records[type]
            const suppliedRows = tables[type]
            if (!previousRows && !suppliedRows) return []
            const retained = new Map<string, Row>(
              (previousRows ?? []).map(record => [record.key, record])
            )
            for (const record of suppliedRows ?? [])
              retained.set(
                record.key,
                mergeSuppliedPackageFields(retained.get(record.key), record) as Row
              )
            for (const removal of pkg.removals)
              if (removal.type === type) retained.delete(removal.key)
            return [[type, [...retained.values()]]]
          })
        ) as TShowPackage['records']
        const retainedPackage: TShowPackage = {
          ...pkg,
          show: mergeSuppliedPackageFields(prior.show, pkg.show) as TShowPackage['show'],
          records: retainedRecords,
          privacyProfile:
            prior.privacyProfile === 'full-contact' || pkg.privacyProfile === 'full-contact'
              ? 'full-contact'
              : 'limited',
        }
        retainedPackage.contentDigest = await computeShowPackageDigest(retainedPackage)
        const retainedCheck = await validatePackage(retainedPackage, { now: options.now }, true)
        resolvedSource = retainedPackage
        if (!retainedCheck.valid)
          for (const diagnostic of retainedCheck.diagnostics) {
            // Retained-only dependencies may be orphaned; supplied unrelated records may not travel.
            const recordPath = /^\/records\/([^/]+)\/(\d+)/.exec(diagnostic.path)
            const suppliedDependency =
              recordPath &&
              tables[recordPath[1] as keyof typeof tables]?.some(
                row =>
                  row.key ===
                  retainedRecords[recordPath[1] as keyof typeof retainedRecords]?.[
                    Number(recordPath[2])
                  ]?.key
              )
            if (
              (diagnostic.code === 'privacy' && suppliedDependency) ||
              ['reference', 'invariant', 'duplicate', 'missing_data', 'structure'].includes(
                diagnostic.code
              )
            )
              problem(
                'baseline',
                `/retained${diagnostic.path}`,
                'Correction leaves invalid retained source data; explicitly update or remove dependent records.'
              )
          }
        for (const old of prior.records.tombstones ?? [])
          if (
            tables.tombstones &&
            !tables.tombstones.some(
              r =>
                r.key === old.key &&
                r.submissionId === old.submissionId &&
                r.deletedEntryKey === old.deletedEntryKey &&
                r.deletedAt === old.deletedAt
            )
          )
            problem(
              'baseline',
              '/records/tombstones',
              'Consumed retry tombstones must be preserved.'
            )
        prior.records.entries?.forEach(old => {
          const next = tables.entries?.find(r => r.key === old.key)
          if (
            !next &&
            (tables.entries || removed.has(`entries:${old.key}`)) &&
            old.submission &&
            !tables.tombstones?.some(
              t => t.submissionId === old.submission?.id && t.deletedEntryKey === old.key
            )
          )
            problem(
              'baseline',
              '/records/tombstones',
              'Deleting a submitted entry requires its retry tombstone.'
            )
          if (
            !next &&
            (tables.entries || removed.has(`entries:${old.key}`)) &&
            !tables.identifiers?.some(
              r => r.value === old.exhibitId && r.source === 'void' && !r.assignedEntryKey
            )
          )
            problem(
              'baseline',
              '/records/identifiers',
              'Deleting an entry must retire its printed identifier.'
            )
          if (
            next &&
            (next.exhibitId !== old.exhibitId ||
              (old.submission !== undefined &&
                next.submission !== undefined &&
                canonicalShowPackageJson(next.submission) !==
                  canonicalShowPackageJson(old.submission)))
          )
            problem(
              'baseline',
              '/records/entries',
              'Established Exhibit IDs and retry identities cannot change.'
            )
        })
        if (tables.identifiers)
          prior.records.identifiers?.forEach(old => {
            const next = tables.identifiers?.find(r => r.key === old.key)
            if (
              !next ||
              next.value !== old.value ||
              next.orderIndex !== old.orderIndex ||
              next.kind !== old.kind ||
              (old.source === 'void' && next.source !== 'void')
            )
              problem(
                'baseline',
                '/records/identifiers',
                'Established identifier inventory cannot be renumbered, removed or recycled.'
              )
          })
      }
    }
  } else if (options.baseline && !baselineOnly)
    problem('baseline', '/previous', 'A baseline is only accepted for a linked correction.')
  return diagnostics.length
    ? { valid: false, diagnostics }
    : { valid: true, package: pkg, diagnostics: [], resolvedSource }
}

/** Mapping choices are explicit admin decisions; names never resolve identity. */
export const ShowPackageReference = z.strictObject({
  packageKey: Key,
  type: z.enum(['show', ...SHOW_PACKAGE_COLLECTIONS]),
  key: Key,
})
export const ShowPackageMappingDecision = z.discriminatedUnion('action', [
  z.strictObject({
    reference: ShowPackageReference,
    action: z.literal('map'),
    destinationId: z.string().min(1).max(1024),
  }),
  z.strictObject({ reference: ShowPackageReference, action: z.literal('create') }),
])
/** Receipt metadata contains no transferred business payload or credentials. */
export const ShowPackageReceipt = z.strictObject({
  receiptKey: Key,
  packageKey: Key,
  revision: Count.min(1),
  showKey: Key,
  contentDigest: Digest,
  destinationInstallationKey: Key,
  appliedAt: Time,
  outcome: z.enum(['applied', 'unchanged']),
})
export type TShowPackageReference = z.infer<typeof ShowPackageReference>
export type TShowPackageMappingDecision = z.infer<typeof ShowPackageMappingDecision>
export type TShowPackageReceipt = z.infer<typeof ShowPackageReceipt>

/** Export is a complete independent snapshot; contacts require an explicit choice. */
export const ShowPackageExportRequest = z.strictObject({
  privacyProfile: z.enum(['limited', 'full-contact']).default('limited'),
})
export type TShowPackageExportRequest = z.infer<typeof ShowPackageExportRequest>
